import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../services/api';

/**
 * PendingTermsBanner
 * ==================
 * Mostra ao dono um banner persistente listando negócios que foram publicados
 * pelo admin (via /admin/diagnostico) mas que ainda precisam do aceite formal
 * de Termos + Política de Privacidade.
 *
 * Sem dados → não renderiza nada. Uso: colocar no topo do /perfil.
 */
export default function PendingTermsBanner() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [aceitaTermos, setAceitaTermos] = useState(false);
  const [aceitaPriv, setAceitaPriv] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/my-businesses/pending-terms');
      setPending(r.data.items || []);
    } catch (_err) {
      // Falha silenciosa: banner é opcional, não deve travar a página
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAceite = (biz) => {
    setSelected(biz);
    setAceitaTermos(false);
    setAceitaPriv(false);
  };

  const handleSubmit = async () => {
    if (!selected) return;
    if (!aceitaTermos || !aceitaPriv) {
      toast.error('É necessário aceitar ambos: Termos e Política de Privacidade.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/businesses/${selected.id}/aceitar-termos-pendente`, {
        aceita_termos_uso: true,
        aceita_politica_privacidade: true,
      });
      toast.success('Termos aceitos. Obrigado!');
      setSelected(null);
      load();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao registrar aceite.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || pending.length === 0) return null;

  return (
    <>
      <div
        className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4"
        data-testid="pending-terms-banner"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">
              {pending.length === 1
                ? 'Seu negócio foi publicado — falta só aceitar os termos'
                : `${pending.length} negócios foram publicados — falta aceitar os termos`}
            </div>
            <p className="text-sm text-amber-900/80 mt-1">
              A administração ativou{pending.length === 1 ? '' : 'ram'} sua página para você não perder visibilidade,
              mas ainda precisamos do seu aceite formal aos <b>Termos de Uso</b> e à{' '}
              <b>Política de Privacidade</b> para deixar tudo regularizado.
            </p>
            <div className="mt-3 space-y-1.5">
              {pending.map((biz) => (
                <div
                  key={biz.id}
                  className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-amber-200"
                  data-testid={`pending-terms-row-${biz.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{biz.nome}</div>
                    <div className="text-xs text-muted-foreground">{biz.categoria || '—'}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => openAceite(biz)}
                    className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
                    data-testid={`accept-terms-btn-${biz.id}`}
                  >
                    Aceitar termos
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="accept-terms-dialog">
          <DialogHeader>
            <DialogTitle>Aceite de termos</DialogTitle>
            <DialogDescription>
              Para manter seu negócio <b>{selected?.nome}</b> publicado, você precisa aceitar
              os documentos abaixo. Um registro auditável (IP, data/hora, dispositivo) será
              gravado como evidência do consentimento (LGPD, Art. 8º).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={aceitaTermos}
                onCheckedChange={setAceitaTermos}
                data-testid="chk-termos-uso"
              />
              <div className="text-sm">
                Li e concordo com os{' '}
                <a href="/termos-de-uso" target="_blank" rel="noreferrer" className="text-purple-600 underline">
                  Termos de Uso
                </a>
                .
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={aceitaPriv}
                onCheckedChange={setAceitaPriv}
                data-testid="chk-politica-privacidade"
              />
              <div className="text-sm">
                Li e concordo com a{' '}
                <a href="/politica-de-privacidade" target="_blank" rel="noreferrer" className="text-purple-600 underline">
                  Política de Privacidade
                </a>
                .
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!aceitaTermos || !aceitaPriv || submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-terms-btn"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrando…</>
                : <><CheckCircle className="w-4 h-4 mr-2" />Confirmar aceite</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
